import { createReducer, on } from '@ngrx/store';
import { Season, SeasonChallengesResponse, SeasonUnclaimedRewards, SeasonLevelsResponse, ActiveSeasonsResponse, SeasonProgressResponse } from '../../models/season.model';
import { SeasonActions } from '../actions/season.actions';

export interface SeasonState {
  activeSeasons: ActiveSeasonsResponse | null;
  season: Season | null;
  progress: SeasonProgressResponse | null;
  challenges: SeasonChallengesResponse | null;
  levels: SeasonLevelsResponse | null;
  unclaimedRewards: SeasonUnclaimedRewards | null;
  loading: boolean;
  error: any;
}

export const initialState: SeasonState = {
  activeSeasons: null,
  season: null,
  progress: null,
  challenges: null,
  levels: null,
  unclaimedRewards: null,
  loading: false,
  error: null
};

export const seasonReducer = createReducer(
  initialState,
  on(SeasonActions.loadActiveSeasons, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadActiveSeasonsSuccess, (state, { response }) => ({ ...state, activeSeasons: response, loading: false })),
  on(SeasonActions.loadActiveSeasonsFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadSeasonInfo, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadSeasonInfoSuccess, (state, { season }) => ({ ...state, season, loading: false })),
  on(SeasonActions.loadSeasonInfoFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadSeasonProgress, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadSeasonProgressSuccess, (state, { response }) => ({
    ...state,
    progress: response,
    season: {
      ...state.season,
      ...response.seasonInfo,
      level: response.currentLevelNumber,
      currentXp: response.currentLevelXp,
      totalXp: response.totalXp,
      isCompleted: response.isSeasonCompleted
    },
    loading: false
  })),
  on(SeasonActions.loadSeasonProgressFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadSeasonChallenges, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadSeasonChallengesSuccess, (state, { response }) => ({ ...state, challenges: response, loading: false })),
  on(SeasonActions.loadSeasonChallengesFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadSeasonLevels, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadSeasonLevelsSuccess, (state, { response }) => ({ ...state, levels: response, loading: false })),
  on(SeasonActions.loadSeasonLevelsFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.loadUnclaimedRewards, (state) => ({ ...state, loading: true })),
  on(SeasonActions.loadUnclaimedRewardsSuccess, (state, { response }) => ({ ...state, unclaimedRewards: response, loading: false })),
  on(SeasonActions.loadUnclaimedRewardsFailure, (state, { error }) => ({ ...state, error, loading: false })),

  on(SeasonActions.claimChallengeSuccess, (state, { challengeId }) => {
    if (!state.challenges) return state;
    const updatedChallenges = state.challenges.challenges.map(ch =>
      ch.rewardId === challengeId
        ? {
            ...ch,
            challengeProgress: ch.challengeProgress
              ? { ...ch.challengeProgress, isRewardClaimed: true }
              : ch.challengeProgress
          }
        : ch
    );
    return {
      ...state,
      challenges: { ...state.challenges, challenges: updatedChallenges }
    };
  }),

  on(SeasonActions.claimSeasonRewardSuccess, (state, { rewardId }) => {
    if (!state.levels) return state;
    const updatedLevels = state.levels.levels.map(level => ({
      ...level,
      rewardsDetails: level.rewardsDetails.map(reward =>
        reward.id === rewardId ? { ...reward, isClaimed: true } : reward
      )
    }));
    return {
      ...state,
      levels: { ...state.levels, levels: updatedLevels }
    };
  }),

  on(SeasonActions.claimAllUnclaimedRewardsSuccess, (state) => ({
    ...state,
    unclaimedRewards: { count: 0 }
  })),
);
